import { useState } from 'react';
import { Heading, Button } from '@simplywallst/ui-core/next';
import EarningsReportOutline from '@simplywallst/ui-core/icons/EarningReportOutline';
import DividendOutline from '@simplywallst/ui-core/icons/DividendOutline';
import PriceUpOutline from '@simplywallst/ui-core/icons/PriceUpOutline';
import InsiderUpOutline from '@simplywallst/ui-core/icons/InsiderUpOutline';
import NewRiskOutline from '@simplywallst/ui-core/icons/NewRiskOutline';
import { useNewsFeed } from './hooks/useNewsFeed';
import { getTotalNewsPerType } from './utils';
import { SidePanel } from './components/SidePanel';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

const FeedCard = loadable(() => import('@/components/FeedCard'));

type FeedType =
  | 'multi-event'
  | 'event'
  | 'article'
  | 'external-article'
  | 'brief'
  | 'narrative';

const company = {
  companyId: 'AF9458F6-A998-44DA-8F53-33A97DAEFBF1',
  name: 'Sony Group',
  tickerSymbol: 'TSE',
  unique_symbol: 'TSE:6758',
  currency_iso: 'JPY',
  sharePriceFormatted: 'US$2,815',
  return7D: -0.022739,
  return1YrAbs: 0.116178,
  canonicalUrl: '/stocks/jp/consumer-durables/tse-6758/sony-group-shares',
  scores: [3, 2, 4, 3, 1] as [number, number, number, number, number],
  feedItems: [
    {
      id: '4101f2dd-8814-11ef-bce9-0a319bc15cb3',
      activityId: '4101f2dd-8814-11ef-bce9-0a319bc15cb3',
      type: 'article' as FeedType,
      title: 'Estimating The Fair Value Of Sony Group Corporation (TSE:6758)',
      subType: '',
      text: "Key Insights Sony Group's estimated fair value is JP\u00a52,820 based on 2 Stage Free Cash Flow to Equity With JP\u00a52,815...",
      topic: 'intrinsic_value',
      url: 'https://simplywall.st/stocks/jp/consumer-durables/tse-6758/sony-group-shares/news/estimating-the-fair-value-of-sony-group-corporation-tse6758',
      thumbnail:
        'https://images.simplywall.st/asset/industry/4111000-choice2-dashboard-thumb/1588573860830',
      date: 1728680604000,
      dateFormatted: '2023-06-12',
      categories: ['valuation'],
    },
    {
      id: '4a7a9a01-7c4b-11ef-bc0e-0affd8f568fb',
      activityId: '4a7a9a01-7c4b-11ef-bc0e-0affd8f568fb',
      type: 'article' as FeedType,
      title:
        "Sony Group's (TSE:6758) Dividend Will Be Increased To \u00a550.00",
      subType: '',
      text: "Sony Group Corporation ( TSE:6758 ) will increase its dividend from last year's comparable payment on the 5th of...",
      topic: 'dividend_change',
      url: 'https://simplywall.st/stocks/jp/consumer-durables/tse-6758/sony-group-shares/news/sony-groups-tse6758-dividend-will-be-increased-to-5000',
      thumbnail:
        'https://images.simplywall.st/asset/industry/4111000-choice2-dashboard-thumb/1588573860830',
      date: 1727384828000,
      dateFormatted: '2023-06-12',
      categories: ['dividend'],
    },
  ],
};

type Props = {
  portfolioId: string;
};

export const NewsFeed = ({ portfolioId }: Props) => {
  const [showSidePanel, setShowSidePanel] = useState(false);

  const { data } = useNewsFeed(portfolioId);
  const totalNewsPerType = getTotalNewsPerType(data);

  const { trackEvent } = useTracking();

  return (
    <>
      <div className="grid grid-cols-[max-content_max-content] grid-rows-[max-content] justify-between items-center h-x8">
        <Heading as="h2" size="small">
          Updates <span className="text-softer text-tiny">Last 30d</span>
        </Heading>
        <Button
          variant="outline"
          size="medium"
          onClick={() => {
            setShowSidePanel(true);
            trackEvent({
              action: 'click',
              modifier: 'OpenNewsFeed',
            });
          }}
        >
          All Updates
        </Button>
      </div>
      <div className="grid grid-cols-[repeat(5,max-content)] md:grid-cols-[repeat(auto-fit,minmax(50px,1fr))] justify-center gap-x1">
        <div className="flex items-center">
          <EarningsReportOutline className="fill-brand-01 w-x3" />
          <div className="text-sm pl-x0_5 pr-x2">
            {totalNewsPerType.HasReportedEarnings || 0}
          </div>
        </div>
        <div className="flex items-center">
          <DividendOutline className="fill-brand-01 w-x3" />
          <div className="text-sm pl-x0_5 pr-x2">
            {totalNewsPerType.HasUpcomingDividend || 0}
          </div>
        </div>
        <div className="flex items-center">
          <PriceUpOutline className="fill-brand-01 w-x3" />
          <div className="text-sm pl-x0_5 pr-x2">
            {totalNewsPerType.HasPriceTargetChanged || 0}
          </div>
        </div>
        <div className="flex items-center">
          <InsiderUpOutline className="fill-brand-01 w-x3" />
          <div className="text-sm pl-x0_5 pr-x2">
            {totalNewsPerType.HasRecentInsiderTransactions || 0}
          </div>
        </div>
        <div className="flex items-center">
          <NewRiskOutline className="fill-brand-01 w-x3" />
          <div className="text-sm pl-x0_5 pr-x2">
            {totalNewsPerType.HasNewRisk || 0}
          </div>
        </div>
      </div>
      {showSidePanel && (
        <SidePanel
          onClose={() => {
            setShowSidePanel(false);
            trackEvent({
              action: 'click',
              modifier: 'CloseNewsFeed',
            });
          }}
        >
          <FeedCard index={1} company={company} />
          <FeedCard index={2} company={company} />
          <FeedCard index={3} company={company} />
          <FeedCard index={4} company={company} />
          <FeedCard index={5} company={company} />
        </SidePanel>
      )}
    </>
  );
};
